import React from "react";
import { Link } from "react-router-dom";
import Logo from "../../components/Logo";
import Footer from "../../components/Footer";
import { ArrowLeft, Construction, Mail } from "lucide-react";

export default function APIDocs() {
  return (
    <div className="au-root">
      <header style={S.header}>
        <Link to="/" style={{ textDecoration: "none" }}><Logo size="medium" /></Link>
        <Link to="/" className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft /> Home
        </Link>
      </header>

      <div className="au-container" style={S.container}>
        <div className="au-eyebrow" style={{ marginBottom: 14 }}>API Documentation</div>
        <h1 style={S.h1}>
          Aegis <span style={{ color: "var(--au-accent-ink)" }}>API</span>
        </h1>
        <p className="au-lead" style={{ marginBottom: 32 }}>
          Programmatic access to Aegis security scanning. Integrate vulnerability detection directly into your CI/CD pipeline.
        </p>

        <div className="au-glass au-pad" style={S.notice}>
          <Construction style={{ width: 20, height: 20, color: "var(--au-warn)", flex: "none", marginTop: 2 }} />
          <div>
            <strong style={{ color: "var(--au-warn)" }}>API Access — Coming Soon:</strong> Full REST API with authentication, rate limiting, and webhooks is currently in development. Available with <strong>Pro+</strong> and <strong>Enterprise</strong> plans on launch. Want early access?{" "}
            <a href="mailto:info@getkhojo.com" style={{ color: "var(--au-accent-ink)" }}>info@getkhojo.com</a>
          </div>
        </div>

        <Section title="Overview">
          <p style={S.p}>
            The Aegis API provides programmatic access to:
          </p>
          <ul style={S.list}>
            <li style={S.li}>Submit security scans for any domain or IP</li>
            <li style={S.li}>Retrieve detailed scan results in JSON format</li>
            <li style={S.li}>Stream scan progress in real-time</li>
            <li style={S.li}>Access historical scan data</li>
            <li style={S.li}>Configure webhooks for scan completion</li>
          </ul>
        </Section>

        <Section title="Authentication">
          <p style={S.p}>API uses bearer token authentication. Generate your API key from the Dashboard.</p>
          <Code>{`curl https://api.getkhojo.com/v1/scans \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        </Section>

        <Section title="Submit a Scan">
          <Method method="POST" endpoint="/api/v1/scans" />
          <Code>{`curl -X POST https://api.getkhojo.com/v1/scans \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "target": "example.com",
    "scan_type": "full"
  }'`}</Code>
        </Section>

        <Section title="Get Scan Results">
          <Method method="GET" endpoint="/api/v1/scans/:id" />
          <Code>{`curl https://api.getkhojo.com/v1/scans/abc123 \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        </Section>

        <Section title="List Scans">
          <Method method="GET" endpoint="/api/v1/scans" />
          <Code>{`curl https://api.getkhojo.com/v1/scans?limit=50 \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</Code>
        </Section>

        <Section title="Webhooks">
          <p style={S.p}>Get notified when scans complete. Configure webhook URL in dashboard.</p>
          <Code>{`{
  "event": "scan.completed",
  "scan_id": "abc123",
  "target": "example.com",
  "risk_score": 67,
  "vulnerabilities_found": 12,
  "timestamp": "2026-04-30T10:00:00Z"
}`}</Code>
        </Section>

        <Section title="Rate Limits">
          <ul style={S.list}>
            <li style={S.li}><strong>Free:</strong> Not available</li>
            <li style={S.li}><strong>Starter:</strong> Not available</li>
            <li style={S.li}><strong>Pro:</strong> 100 requests/hour</li>
            <li style={S.li}><strong>Pro+:</strong> 500 requests/hour</li>
            <li style={S.li}><strong>Enterprise:</strong> 2,000 requests/hour</li>
          </ul>
        </Section>

        <Section title="SDKs (Coming Soon)">
          <ul style={S.list}>
            <li style={S.li}>Python (priority)</li>
            <li style={S.li}>JavaScript / Node.js</li>
            <li style={S.li}>Go</li>
            <li style={S.li}>Ruby</li>
          </ul>
        </Section>

        <div className="au-glass au-pad-lg au-center" style={{ marginTop: 16, marginBottom: 48 }}>
          <h2 style={{ ...S.h2, marginTop: 0 }}>Get Early API Access</h2>
          <p style={{ ...S.p, marginBottom: 18 }}>Join the waitlist for Pro+ API beta.</p>
          <a href="mailto:info@getkhojo.com?subject=API%20Beta%20Access" className="au-pill au-pill-primary">
            <Mail /> Request Beta Access
          </a>
        </div>

      </div>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={S.section}>
      <h2 style={S.h2}>{title}</h2>
      {children}
    </section>
  );
}

function Method({ method, endpoint }) {
  const colors = {
    GET: "var(--au-success)",
    POST: "var(--au-info)",
    PUT: "var(--au-warn)",
    DELETE: "var(--au-error)"
  };
  const c = colors[method] || "var(--au-accent-ink)";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
      <span className="au-mono" style={{ padding: "4px 12px", background: "var(--au-glass-strong)", color: c, border: `1px solid ${c}`, borderRadius: 6, fontWeight: 700, fontSize: 12 }}>{method}</span>
      <code className="au-mono" style={{ color: "var(--au-text)", fontSize: 14 }}>{endpoint}</code>
    </div>
  );
}

function Code({ children }) {
  return (
    <pre className="au-glass au-mono" style={{ padding: 18, fontSize: 13, color: "var(--au-accent-ink)", fontFamily: "var(--au-font-mono)", overflow: "auto", lineHeight: 1.6, margin: 0 }}>{children}</pre>
  );
}

const S = {
  header: { maxWidth: 900, margin: "0 auto", padding: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 },
  container: { maxWidth: 900, padding: "40px 24px 60px" },
  h1: { fontSize: "clamp(2rem, 5vw, 3rem)", margin: "0 0 24px" },
  h2: { fontSize: "clamp(1.3rem, 2.6vw, 1.5rem)", color: "var(--au-text)", margin: "0 0 16px" },
  p: { fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.8, marginBottom: 14 },
  notice: { display: "flex", gap: 12, alignItems: "flex-start", fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.7, marginBottom: 40 },
  section: { marginBottom: 36 },
  list: { paddingLeft: 24, marginBottom: 14 },
  li: { fontSize: 14, color: "var(--au-text-2)", lineHeight: 1.8, marginBottom: 8 },
  footer: { textAlign: "center", padding: "40px 0 20px", color: "var(--au-muted)", fontSize: 12, borderTop: "1px solid var(--au-hair)", marginTop: 40 }
};
